import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc,
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  PipelineCard, 
  PipelineStageData, 
  PipelineMetrics, 
  PipelineFilters,
  PipelineStage,
  PIPELINE_STAGE_CONFIG
} from '../types/pipeline';
import { Quotation } from '../types/quotation';
import { Order } from '../types/order';
import { DeliveryNote } from '../types/deliveryNote';

/**
 * Pipeline Service - HubSpot-szerű pipeline nézet adatkezelése
 */
export const pipelineService = {
  /**
   * Pipeline adatok lekérése (Quotations, Orders, DeliveryNotes, Invoices)
   */
  async getPipelineData(
    organizationId: string, 
    filters?: PipelineFilters
  ): Promise<PipelineStageData[]> {
    try {
      // Párhuzamos lekérdezések
      const [quotations, orders, deliveryNotes, invoices] = await Promise.all([
        this.getQuotations(organizationId, filters),
        this.getOrders(organizationId, filters),
        this.getDeliveryNotes(organizationId, filters),
        this.getInvoices(organizationId, filters)
      ]);

      // Átalakítás PipelineStageData formátumra
      const stages: PipelineStageData[] = [
        {
          stage: 'quotations',
          title: PIPELINE_STAGE_CONFIG.quotations.title,
          cards: quotations,
          totalValue: quotations.reduce((sum, card) => sum + card.totalAmount, 0),
          count: quotations.length,
          color: PIPELINE_STAGE_CONFIG.quotations.color
        },
        {
          stage: 'orders',
          title: PIPELINE_STAGE_CONFIG.orders.title,
          cards: orders,
          totalValue: orders.reduce((sum, card) => sum + card.totalAmount, 0),
          count: orders.length,
          color: PIPELINE_STAGE_CONFIG.orders.color
        },
        {
          stage: 'deliveries',
          title: PIPELINE_STAGE_CONFIG.deliveries.title,
          cards: deliveryNotes,
          totalValue: deliveryNotes.reduce((sum, card) => sum + card.totalAmount, 0),
          count: deliveryNotes.length,
          color: PIPELINE_STAGE_CONFIG.deliveries.color
        },
        {
          stage: 'invoices',
          title: PIPELINE_STAGE_CONFIG.invoices.title,
          cards: invoices,
          totalValue: invoices.reduce((sum, card) => sum + card.totalAmount, 0),
          count: invoices.length,
          color: PIPELINE_STAGE_CONFIG.invoices.color
        }
      ];

      return stages;
    } catch (error) {
      console.error('Error getting pipeline data:', error);
      throw error;
    }
  },

  /**
   * Quotations lekérése
   */
  async getQuotations(organizationId: string, filters?: PipelineFilters): Promise<PipelineCard[]> {
    const quotationsRef = collection(db, 'quotations');
    let q = query(quotationsRef, where('organizationId', '==', organizationId));

    // Szűrők alkalmazása
    if (filters?.customerId) {
      q = query(q, where('customerId', '==', filters.customerId));
    }

    const snapshot = await getDocs(q);
    const cards: PipelineCard[] = [];

    snapshot.forEach(doc => {
      const data = doc.data() as Quotation;
      
      // További szűrések (client-side)
      if (this.applyFilters(data, filters)) {
        cards.push(this.quotationToCard(doc.id, data));
      }
    });

    return cards;
  },

  /**
   * Orders lekérése
   */
  async getOrders(organizationId: string, filters?: PipelineFilters): Promise<PipelineCard[]> {
    const ordersRef = collection(db, 'orders');
    let q = query(ordersRef, where('organizationId', '==', organizationId));

    if (filters?.customerId) {
      q = query(q, where('customerId', '==', filters.customerId));
    }

    const snapshot = await getDocs(q);
    const cards: PipelineCard[] = [];

    snapshot.forEach(doc => {
      const data = doc.data() as Order;
      
      if (this.applyFilters(data, filters)) {
        cards.push(this.orderToCard(doc.id, data));
      }
    });

    return cards;
  },

  /**
   * DeliveryNotes lekérése
   */
  async getDeliveryNotes(organizationId: string, filters?: PipelineFilters): Promise<PipelineCard[]> {
    const deliveryNotesRef = collection(db, 'deliveryNotes');
    let q = query(deliveryNotesRef, where('organizationId', '==', organizationId));

    if (filters?.customerId) {
      q = query(q, where('customerId', '==', filters.customerId));
    }

    const snapshot = await getDocs(q);
    const cards: PipelineCard[] = [];

    snapshot.forEach(doc => {
      const data = doc.data() as DeliveryNote;
      
      if (this.applyFilters(data, filters)) {
        cards.push(this.deliveryNoteToCard(doc.id, data));
      }
    });

    return cards;
  },

  /**
   * Invoices lekérése
   */
  async getInvoices(organizationId: string, filters?: PipelineFilters): Promise<PipelineCard[]> {
    // TODO: Invoice collection létrehozása után implementálni
    return [];
  },

  /**
   * Quotation → PipelineCard konverzió
   */
  quotationToCard(id: string, quotation: Quotation): PipelineCard {
    return {
      id,
      organizationId: quotation.organizationId,
      documentType: 'quotation',
      documentNumber: quotation.number || quotation.quotationNumber || id,
      stage: 'quotations',
      customerName: quotation.customerName,
      customerId: quotation.customerId,
      totalAmount: quotation.totalAmount,
      currency: quotation.currency,
      status: quotation.status,
      createdAt: new Date(quotation.createdAt),
      updatedAt: new Date(quotation.updatedAt),
      notes: quotation.notes,
      documentCount: quotation.documents?.length || 0
    };
  },

  /**
   * Order → PipelineCard konverzió
   */
  orderToCard(id: string, order: Order): PipelineCard {
    return {
      id,
      organizationId: order.organizationId,
      documentType: 'order',
      documentNumber: order.number,
      stage: 'orders',
      customerName: order.customerName,
      customerId: order.customerId,
      totalAmount: order.totalAmount,
      currency: order.currency,
      status: order.status,
      createdAt: new Date(order.createdAt),
      updatedAt: new Date(order.updatedAt),
      quotationId: order.quotationId,
      notes: order.notes,
      documentCount: order.documents?.length || 0
    };
  },

  /**
   * DeliveryNote → PipelineCard konverzió
   */
  deliveryNoteToCard(id: string, deliveryNote: DeliveryNote): PipelineCard {
    return {
      id,
      organizationId: deliveryNote.organizationId,
      documentType: 'delivery_note',
      documentNumber: deliveryNote.number,
      stage: 'deliveries',
      customerName: deliveryNote.customerName,
      customerId: deliveryNote.customerId,
      totalAmount: deliveryNote.totalAmount || 0,
      currency: deliveryNote.currency,
      status: deliveryNote.status,
      createdAt: new Date(deliveryNote.createdAt),
      updatedAt: new Date(deliveryNote.updatedAt),
      orderId: deliveryNote.orderId,
      quotationId: deliveryNote.quotationId,
      notes: deliveryNote.notes,
      documentCount: deliveryNote.documents?.length || 0
    };
  },

  /**
   * Szűrők alkalmazása
   */
  applyFilters(data: any, filters?: PipelineFilters): boolean {
    if (!filters) return true;

    // Dátum szűrés
    if (filters.dateFrom && new Date(data.createdAt) < filters.dateFrom) return false;
    if (filters.dateTo && new Date(data.createdAt) > filters.dateTo) return false;

    // Érték szűrés
    if (filters.minValue && data.totalAmount < filters.minValue) return false;
    if (filters.maxValue && data.totalAmount > filters.maxValue) return false;

    // Keresés
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch = 
        data.customerName?.toLowerCase().includes(searchLower) ||
        data.number?.toLowerCase().includes(searchLower) ||
        data.notes?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    return true;
  },

  /**
   * Kártya mozgatása stage-ek között
   */
  async moveCardToStage(
    cardId: string,
    fromStage: PipelineStage,
    toStage: PipelineStage
  ): Promise<void> {
    // TODO: Státusz frissítés a megfelelő collection-ben
    // TODO: Esetleg automatikus konverzió (Q→O, O→D, D→I)
    console.log(`Moving card ${cardId} from ${fromStage} to ${toStage}`);
  },

  /**
   * Dokumentum konverzió
   */
  async convertDocument(
    cardId: string,
    fromType: string,
    toType: string
  ): Promise<void> {
    // TODO: Cloud Function hívás a konverzióhoz
    console.log(`Converting document ${cardId} from ${fromType} to ${toType}`);
  },

  /**
   * Metrikák számítása
   */
  async calculateMetrics(
    organizationId: string,
    filters?: PipelineFilters
  ): Promise<PipelineMetrics> {
    const stages = await this.getPipelineData(organizationId, filters);

    const totalValue = stages.reduce((sum, stage) => sum + stage.totalValue, 0);
    const totalCount = stages.reduce((sum, stage) => sum + stage.count, 0);

    // Konverziós ráták számítása
    const quotationsCount = stages.find(s => s.stage === 'quotations')?.count || 0;
    const ordersCount = stages.find(s => s.stage === 'orders')?.count || 0;
    const deliveriesCount = stages.find(s => s.stage === 'deliveries')?.count || 0;
    const invoicesCount = stages.find(s => s.stage === 'invoices')?.count || 0;

    const quotationToOrder = quotationsCount > 0 ? (ordersCount / quotationsCount) * 100 : 0;
    const orderToDelivery = ordersCount > 0 ? (deliveriesCount / ordersCount) * 100 : 0;
    const deliveryToInvoice = deliveriesCount > 0 ? (invoicesCount / deliveriesCount) * 100 : 0;
    const overall = quotationsCount > 0 ? (invoicesCount / quotationsCount) * 100 : 0;

    return {
      organizationId,
      totalValue,
      totalCount,
      currency: 'HUF', // TODO: Dinamikus
      stages: {
        quotations: { 
          count: stages.find(s => s.stage === 'quotations')?.count || 0,
          value: stages.find(s => s.stage === 'quotations')?.totalValue || 0
        },
        orders: { 
          count: stages.find(s => s.stage === 'orders')?.count || 0,
          value: stages.find(s => s.stage === 'orders')?.totalValue || 0
        },
        deliveries: { 
          count: stages.find(s => s.stage === 'deliveries')?.count || 0,
          value: stages.find(s => s.stage === 'deliveries')?.totalValue || 0
        },
        invoices: { 
          count: stages.find(s => s.stage === 'invoices')?.count || 0,
          value: stages.find(s => s.stage === 'invoices')?.totalValue || 0
        }
      },
      conversionRate: {
        quotationToOrder,
        orderToDelivery,
        deliveryToInvoice,
        overall
      },
      averageValue: totalCount > 0 ? totalValue / totalCount : 0,
      averageDealSize: {
        quotations: quotationsCount > 0 ? (stages.find(s => s.stage === 'quotations')?.totalValue || 0) / quotationsCount : 0,
        orders: ordersCount > 0 ? (stages.find(s => s.stage === 'orders')?.totalValue || 0) / ordersCount : 0,
        deliveries: deliveriesCount > 0 ? (stages.find(s => s.stage === 'deliveries')?.totalValue || 0) / deliveriesCount : 0,
        invoices: invoicesCount > 0 ? (stages.find(s => s.stage === 'invoices')?.totalValue || 0) / invoicesCount : 0
      },
      averageDuration: {
        quotationToOrder: 0, // TODO: Számítás
        orderToDelivery: 0,
        deliveryToInvoice: 0,
        totalCycleTime: 0
      }
    };
  }
};
